import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { PageHeader, StatusBadge, HoldBadge, EmptyState, carrierLabel, CARRIER_OPTIONS, CarrierBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Truck, Plus, MagnifyingGlass, ArrowsMerge, Scan, ArrowsClockwise } from "@phosphor-icons/react";

const FILTER_KEY = "shipments.filters";
const readFilters = () => { try { return JSON.parse(sessionStorage.getItem(FILTER_KEY)) || {}; } catch { return {}; } };
const STATUS_TABS = [["preparing", "Preparing"], ["dispatched", "Dispatched"], ["in_transit", "In transit"], ["arrived", "Arrived"], ["delivered", "Delivered"]];

export default function Shipments() {
  const navigate = useNavigate();
  const { isTeam, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backfillAwb = async () => {
    try {
      const { data } = await api.post("/shipments/backfill-awb");
      toast.success(data.queued ? `Reading AWB numbers for ${data.queued} shipment(s) in the background — refresh in a minute` : "All shipments with AWB documents already have a number");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const { t } = useT();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ order_id: "", carrier: "", tracking_number: "", eta: "", origin: "", destination: "", patient_name: "" });
  const [bulkQty, setBulkQty] = useState([]);
  const selOrder = orders.find((o) => o.id === form.order_id);
  const selBulk = selOrder?.supply_type === "bulk";
  const pickOrder = (v) => {
    const o = orders.find((x) => x.id === v);
    setForm({ ...form, order_id: v, carrier: form.carrier || o?.preferred_carrier || "" });
    setBulkQty((o?.items || []).map((it) => Math.max(0, (Number(it.quantity) || 0) - (Number(it.shipped) || 0))));
  };
  const savedFilters = readFilters();
  const [search, setSearch] = useState(() => savedFilters.search || "");
  const [clientFilter, setClientFilter] = useState(() => savedFilters.clientFilter || "all");
  const [params] = useSearchParams();
  const urlStatus = params.get("status");
  const [statusTab, setStatusTab] = useState(() => (urlStatus && ["all", "active", ...STATUS_TABS.map(([v]) => v)].includes(urlStatus) ? urlStatus : savedFilters.statusTab || "all"));
  useEffect(() => { sessionStorage.setItem(FILTER_KEY, JSON.stringify({ search, clientFilter, statusTab })); }, [search, clientFilter, statusTab]);
  const [cOpen, setCOpen] = useState(false);
  const [cOrderIds, setCOrderIds] = useState([]);
  const [cForm, setCForm] = useState({ carrier: "", tracking_number: "", eta: "", origin: "", destination: "" });
  const [cBusy, setCBusy] = useState(false);
  const cToggle = (id) => setCOrderIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSel, setMergeSel] = useState([]);
  const [mOpen, setMOpen] = useState(false);
  const [mForm, setMForm] = useState({ carrier: "", tracking_number: "", eta: "", origin: "", destination: "" });
  const [mBusy, setMBusy] = useState(false);
  const mToggle = (s) => setMergeSel((p) => (p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id]));

  const [refreshing, setRefreshing] = useState(false);
  const load = () => api.get("/shipments").then((r) => {
    setShipments(r.data);
    if (r.headers["x-tracking-refreshing"] === "1") { setRefreshing(true); setTimeout(() => api.get("/shipments").then((r2) => setShipments(r2.data)).catch(() => {}).finally(() => setRefreshing(false)), 12000); }
  }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, [isTeam]);

  const submit = async () => {
    if (!form.order_id) return toast.error("Select an order");
    const payload = { ...form };
    if (selBulk) {
      payload.line_items = (selOrder.items || []).map((it, i) => ({ name: it.name, sku: it.sku || "", quantity: Number(bulkQty[i]) || 0 }));
      if (payload.line_items.every((l) => l.quantity <= 0)) return toast.error("Enter a quantity for at least one product to ship");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shipments", payload);
      toast.success(`Shipment ${data.shipment_number} created`);
      setOpen(false);
      setForm({ order_id: "", carrier: "", tracking_number: "", eta: "", origin: "", destination: "", patient_name: "" });
      navigate(`/shipments/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const removeShipment = async (id, number) => {
    await api.delete(`/shipments/${id}`);
    toast.success(`Shipment ${number} deleted`);
    load();
  };

  const brazilApproved = orders.filter((o) => o.preferred_carrier === "brazil_courier" && o.status === "approved");
  const cSelClient = cOrderIds.length ? brazilApproved.find((o) => o.id === cOrderIds[0])?.client_id : null;
  const createConsolidated = async () => {
    if (cOrderIds.length < 2) return toast.error("Select at least two Brazil Courier orders");
    setCBusy(true);
    try {
      const { data } = await api.post("/shipments/consolidated", { order_ids: cOrderIds, ...cForm });
      toast.success(`Shipment ${data.shipment_number} created (${cOrderIds.length} orders)`);
      setCOpen(false); setCOrderIds([]); setCForm({ carrier: "", tracking_number: "", eta: "", origin: "", destination: "" });
      navigate(`/shipments/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setCBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const clients = [...new Set(shipments.map((s) => s.client_name).filter(Boolean))].sort();
  const filtered = shipments.filter((s) => (clientFilter === "all" || s.client_name === clientFilter) &&
    (statusTab === "all" || (statusTab === "active" ? ["dispatched", "in_transit", "arrived"].includes(s.status) : (s.status || "preparing") === statusTab)) &&
    (!q || [s.tracking_number, s.shipment_number, s.order_number, s.client_name, s.client_reference, s.destination, s.patient_name, ...(s.product_names || (s.line_items || []).map((i) => i.name))].some((v) => (v || "").toLowerCase().includes(q))));
  const hasFilters = q || clientFilter !== "all" || statusTab !== "all";
  const clearFilters = () => { setSearch(""); setClientFilter("all"); setStatusTab("all"); };

  const selShips = shipments.filter((s) => mergeSel.includes(s.id));
  const orderById = Object.fromEntries(orders.map((o) => [o.id, o]));
  const shipItems = (s) => {
    if (s.line_items?.length) return s.line_items.map((li) => `${li.quantity}× ${li.name}`);
    const ids = s.order_ids?.length ? s.order_ids : (s.order_id ? [s.order_id] : []);
    const items = [];
    ids.forEach((id) => (orderById[id]?.items || []).forEach((it) => items.push(`${it.quantity}× ${it.name}`)));
    return items;
  };
  const mAnchor = selShips[0];
  const selectable = (s) => (s.status || "preparing") === "preparing" && (!mAnchor || (s.client_id === mAnchor.client_id && (s.carrier || "") === (mAnchor.carrier || "")));
  const doMerge = async () => {
    if (mergeSel.length < 2) return toast.error("Select at least two shipments");
    setMBusy(true);
    try {
      const { data } = await api.post("/shipments/merge", { shipment_ids: mergeSel, ...mForm });
      toast.success(`Merged into shipment ${data.shipment_number}`);
      setMOpen(false); setMergeMode(false); setMergeSel([]); setMForm({ carrier: "", tracking_number: "", eta: "", origin: "", destination: "" });
      navigate(`/shipments/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setMBusy(false); }
  };

  const topRef = useRef(null);
  const bodyRef = useRef(null);
  const [scrollW, setScrollW] = useState(0);
  useEffect(() => { if (bodyRef.current) setScrollW(bodyRef.current.scrollWidth); }, [shipments, mergeMode, isTeam]);
  const syncTop = () => { if (bodyRef.current && topRef.current) bodyRef.current.scrollLeft = topRef.current.scrollLeft; };
  const syncBody = () => { if (bodyRef.current && topRef.current) topRef.current.scrollLeft = bodyRef.current.scrollLeft; };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle={refreshing ? <span className="inline-flex items-center gap-1.5" data-testid="tracking-refreshing"><ArrowsClockwise size={14} className="animate-spin" /> Refreshing FedEx / UPS scans…</span> : t("ship.subtitle")}
        title={isTeam ? "Shipments" : t("nav.shipments")}
        actions={isTeam && (
          <div className="flex gap-2">
            {isAdmin && <Button variant="ghost" className="rounded-sm text-muted-foreground" onClick={backfillAwb} title="Read AWB numbers from documents on shipments that still have no tracking number" data-testid="backfill-awb"><Scan size={16} className="mr-2" /> Read AWB numbers</Button>}
            <Button variant={mergeMode ? "default" : "outline"} className="rounded-sm" onClick={() => { setMergeMode((m) => !m); setMergeSel([]); }} data-testid="merge-mode-toggle"><ArrowsMerge size={16} className="mr-2" /> {mergeMode ? "Cancel merge" : "Merge shipments"}</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => setCOpen(true)} data-testid="consolidate-shipment-button"><Plus size={16} className="mr-2" /> Consolidate (Brazil)</Button>
            <Button className="rounded-sm" onClick={() => setOpen(true)} data-testid="new-shipment-button"><Plus size={16} className="mr-2" /> New Shipment</Button>
          </div>
        )}
      />
      {!loading && shipments.length === 0 ? (
        <EmptyState title={isTeam ? "No shipments yet" : t("ship.noneTitle")} hint={isTeam ? "Create a shipment for an order, attach carrier & AWB, and upload batch/expiry photo evidence." : t("ship.noneHintClient")} action={isTeam && <Button className="rounded-sm" onClick={() => setOpen(true)}>New Shipment</Button>} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative w-full max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9 rounded-sm" placeholder={isTeam ? "Search by AWB, shipment #, ref #, order #, client or product…" : t("ship.searchPh")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="shipments-search" />
            </div>
            {isTeam && clients.length > 0 && (
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="rounded-sm w-full sm:w-56" data-testid="shipments-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All clients</SelectItem>
                  {clients.map((c) => <SelectItem key={c} value={c} data-testid={`shipments-client-opt-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {isTeam && hasFilters && (
              <>
                <span className="text-xs text-muted-foreground" data-testid="shipments-filter-count">{filtered.length} of {shipments.length}</span>
                <button type="button" onClick={clearFilters} className="text-xs text-primary hover:underline" data-testid="shipments-clear-filters">Clear filters</button>
              </>
            )}
          </div>
          {isTeam && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="shipments-status-tabs">
              <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground w-24 shrink-0">Status</span>
              {statusTab === "active" && <span className="px-2.5 py-1 text-xs font-medium rounded-sm border bg-primary text-primary-foreground border-primary" data-testid="shipments-tab-active">In transit (all active) <button type="button" className="ml-1 opacity-80" onClick={() => setStatusTab("all")}>×</button></span>}
              {STATUS_TABS.map(([v, l]) => {
                const count = shipments.filter((s) => (s.status || "preparing") === v).length;
                const active = statusTab === v;
                return (
                  <button key={v} type="button" onClick={() => setStatusTab(active ? "all" : v)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"}`}
                    data-testid={`shipments-tab-${v}`}>
                    {l} <span className={`ml-1 tabular-nums ${active ? "opacity-80" : "opacity-60"}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        {isTeam && mergeMode && (
          <div className="flex flex-wrap items-center gap-3 border border-primary/30 bg-primary/5 rounded-sm px-3 py-2" data-testid="merge-toolbar">
            <span className="text-sm font-medium">{mergeSel.length} selected</span>
            <span className="text-xs text-muted-foreground">Tick 2+ ‘preparing’ shipments from the same client & carrier to combine them under one AWB.</span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setMergeSel([])} disabled={!mergeSel.length} data-testid="merge-clear">Clear</Button>
              <Button size="sm" className="rounded-sm" disabled={mergeSel.length < 2} onClick={() => { setMForm((f) => ({ ...f, carrier: mAnchor?.carrier || "", tracking_number: mAnchor?.tracking_number || "" })); setMOpen(true); }} data-testid="open-merge-dialog">Merge{mergeSel.length ? ` (${mergeSel.length})` : ""}</Button>
            </div>
          </div>
        )}
        <div className="md:hidden" data-testid="shipments-mobile-list">
          {filtered.map((s) => (
            <div key={s.id} className={`m-card cursor-pointer ${mergeMode && !selectable(s) ? "opacity-40" : ""}`} onClick={() => (mergeMode ? (selectable(s) && mToggle(s)) : navigate(`/shipments/${s.id}`))} data-testid={`shipment-card-${s.shipment_number}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex items-start gap-2">
                  {isTeam && mergeMode && <input type="checkbox" className="mt-1 h-4 w-4" checked={mergeSel.includes(s.id)} disabled={!selectable(s)} onChange={() => mToggle(s)} onClick={(e) => e.stopPropagation()} />}
                  <div className="min-w-0">
                    <p className="font-heading font-bold flex items-center gap-1.5"><Truck size={15} className="text-primary shrink-0" />{s.shipment_number}{s.order_ids?.length > 1 && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold">×{s.order_ids.length}</span>}</p>
                    <p className="text-sm text-muted-foreground truncate">{s.order_number}{isTeam && s.client_name ? ` · ${s.client_name}` : ""}</p>
                    {s.is_bulk && s.patient_name && <p className="text-xs text-muted-foreground truncate">PTN: <span className="text-foreground font-medium">{s.patient_name}</span></p>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1"><StatusBadge status={s.status} /><HoldBadge shipment={s} testid={`ship-hold-card-${s.shipment_number}`} /></div>
              </div>
              <div className="mt-2.5 flex items-center justify-between gap-2 text-sm">
                <span className="truncate flex items-center gap-1"><CarrierBadge carrier={s.carrier} />{s.tracking_number ? <span className="text-muted-foreground"> · AWB {s.tracking_number}</span> : ""}</span>
                <span className="text-xs text-muted-foreground shrink-0">{s.photos?.length || 0} {t("ship.colPhotos").toLowerCase()}</span>
              </div>
              {shipItems(s).length > 0 && <p className="mt-1.5 text-xs text-muted-foreground truncate">{shipItems(s).slice(0, 2).join(", ")}{shipItems(s).length > 2 ? ` +${shipItems(s).length - 2}` : ""}</p>}
            </div>
          ))}
          {filtered.length === 0 && <p className="py-10 text-center text-muted-foreground">{q ? `No shipments match "${search}".` : "No shipments match the selected filters."}</p>}
        </div>
        <div className="space-y-0 hidden md:block">
          <div ref={topRef} onScroll={syncTop} className="overflow-x-auto border border-b-0 border-border rounded-t-md bg-card" data-testid="shipments-top-scroll"><div style={{ width: scrollW, height: 1 }} /></div>
          <div ref={bodyRef} onScroll={syncBody} className="border border-border rounded-b-md overflow-x-auto bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
                {isTeam && mergeMode && <th className="px-4 py-3 w-10"></th>}
                <th className="px-4 py-3 font-semibold">{t("ship.colShipment")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.order")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.client")}</th>
                <th className="px-4 py-3 font-semibold">{t("ship.colCarrier")}</th>
                <th className="px-4 py-3 font-semibold">{t("ship.colPhotos")}</th>
                <th className="px-4 py-3 font-semibold">{t("common.status")}</th>
                <th className="px-4 py-3 font-semibold">Items</th>
                {isTeam && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => (
                <tr key={s.id} onClick={() => (mergeMode ? (selectable(s) && mToggle(s)) : navigate(`/shipments/${s.id}`))} className={`cursor-pointer hover:bg-primary/5 transition-colors ${i % 2 ? "bg-muted/30" : ""} ${mergeMode && !selectable(s) ? "opacity-40" : ""}`} data-testid={`shipment-row-${s.shipment_number}`}>
                  {isTeam && mergeMode && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={mergeSel.includes(s.id)} disabled={!selectable(s)} onChange={() => mToggle(s)} data-testid={`merge-cb-${s.shipment_number}`} />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">
                    <div className="flex items-center gap-2">
                      <Truck size={16} className="text-primary" />
                      {s.tracking_url ? (
                        <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline" data-testid={`shipment-track-link-${s.shipment_number}`}>
                          {s.shipment_number} ↗
                        </a>
                      ) : (
                        s.shipment_number
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{s.order_numbers?.length > 1 ? `${s.order_number}` : s.order_number}</span>
                      {s.carrier === "brazil_courier" && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`ship-badge-brazil-${s.shipment_number}`}>Brazil</span>}
                      {s.order_ids?.length > 1 && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`ship-badge-consolidated-${s.shipment_number}`}>×{s.order_ids.length}</span>}
                    </div>
                    {s.is_bulk && s.patient_name && <p className="text-xs text-muted-foreground mt-0.5" data-testid={`ship-ptn-${s.shipment_number}`}>PTN: <span className="text-foreground font-medium">{s.patient_name}</span></p>}
                  </td>
                  <td className="px-4 py-3">{s.client_name}</td>
                  <td className="px-4 py-3">
                    <div><CarrierBadge carrier={s.carrier} /></div>
                    {s.tracking_number && (s.tracking_url ? (
                      <a onClick={(e) => e.stopPropagation()} href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline" data-testid={`awb-link-${s.shipment_number}`}>AWB {s.tracking_number} ↗</a>
                    ) : (
                      <span className="text-xs text-muted-foreground">AWB {s.tracking_number}</span>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{s.photos?.length || 0}</td>
                  <td className="px-4 py-3"><div className="flex flex-col items-start gap-1"><StatusBadge status={s.status} /><HoldBadge shipment={s} testid={`ship-hold-${s.shipment_number}`} /></div></td>
                  <td className="px-4 py-3" data-testid={`shipment-items-${s.shipment_number}`}>
                    {(() => {
                      const items = shipItems(s);
                      return items.length ? (
                        <div className="text-xs text-muted-foreground max-w-[220px]">
                          {items.slice(0, 3).map((it, idx) => <div key={idx} className="truncate">{it}</div>)}
                          {items.length > 3 && <div className="text-[10px] opacity-70">+{items.length - 3} more</div>}
                        </div>
                      ) : <span className="text-muted-foreground">—</span>;
                    })()}
                  </td>
                  {isTeam && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <ConfirmDelete
                        testid={`delete-shipment-${s.shipment_number}`}
                        title={`Delete shipment ${s.shipment_number}?`}
                        description="This permanently removes the shipment, its photos and AWB records. This cannot be undone."
                        onConfirm={() => removeShipment(s.id, s.shipment_number)}
                      />
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={(isTeam ? 8 : 7) + (isTeam && mergeMode ? 1 : 0)} className="px-4 py-10 text-center text-muted-foreground" data-testid="shipments-no-results">{q ? `No shipments match "${search}".` : "No shipments match the selected filters."}</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Shipment</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide">Order *</Label>
              <Select value={form.order_id} onValueChange={pickOrder}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="new-ship-order"><SelectValue placeholder="Select order" /></SelectTrigger>
                <SelectContent>
                  {orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.order_number} — {o.client_name}{o.supply_type === "bulk" ? " · Bulk" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selBulk && (
              <div className="col-span-2 border border-border rounded-md overflow-hidden" data-testid="new-ship-bulk-picker">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                  <div className="col-span-6">Product</div>
                  <div className="col-span-3 text-right">Remaining</div>
                  <div className="col-span-3 text-right">Ship now</div>
                </div>
                {(selOrder.items || []).map((it, i) => {
                  const remaining = Math.max(0, (Number(it.quantity) || 0) - (Number(it.shipped) || 0));
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center">
                      <div className="col-span-6 text-sm truncate">{it.name}</div>
                      <div className="col-span-3 text-right text-sm text-muted-foreground">{remaining}</div>
                      <Input type="number" min="0" max={remaining} className="col-span-3 rounded-sm h-9 text-right" value={bulkQty[i] ?? 0} onChange={(e) => setBulkQty((p) => p.map((v, idx) => (idx === i ? e.target.value : v)))} data-testid={`new-ship-bulk-qty-${i}`} />
                    </div>
                  );
                })}
                <div className="px-3 pb-3 pt-1">
                  <Label className="text-xs uppercase tracking-wide">Patient (PTN) name for this shipment</Label>
                  <Input className="mt-1.5 rounded-sm" placeholder="Optional — e.g. John Doe" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="new-ship-ptn" />
                </div>
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wide">Carrier</Label>
              <Select value={form.carrier} onValueChange={(v) => setForm({ ...form, carrier: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="new-ship-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                <SelectContent>
                  {CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wide">AWB / Tracking #</Label><Input className="mt-1.5 rounded-sm" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} data-testid="new-ship-tracking" /></div>
            <div><Label className="text-xs uppercase tracking-wide">ETA</Label><Input type="date" className="mt-1.5 rounded-sm" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} data-testid="new-ship-eta" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Origin</Label><Input className="mt-1.5 rounded-sm" value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="new-ship-origin" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Destination</Label><Input className="mt-1.5 rounded-sm" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="new-ship-destination" /></div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="submit-new-shipment">{busy ? "Creating…" : "Create Shipment"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Consolidate shipment (Brazil Courier)</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide">Orders</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Tick 2+ approved Brazil Courier orders from the same client to ship under one AWB.</p>
              <div className="max-h-44 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {brazilApproved.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No approved Brazil Courier orders available.</p>}
                {brazilApproved.map((o) => {
                  const disabled = cSelClient && o.client_id !== cSelClient;
                  return (
                    <label key={o.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${disabled ? "opacity-40" : "hover:bg-primary/5"}`} data-testid={`consol-ship-order-${o.order_number}`}>
                      <input type="checkbox" checked={cOrderIds.includes(o.id)} disabled={disabled} onChange={() => cToggle(o.id)} data-testid={`consol-ship-cb-${o.order_number}`} />
                      <span className="flex-1">{o.order_number} — {o.client_name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wide">Carrier</Label>
                <Select value={cForm.carrier} onValueChange={(v) => setCForm({ ...cForm, carrier: v })}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="consol-ship-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                  <SelectContent>{CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wide">AWB / Tracking #</Label><Input className="mt-1.5 rounded-sm" value={cForm.tracking_number} onChange={(e) => setCForm({ ...cForm, tracking_number: e.target.value })} data-testid="consol-ship-tracking" /></div>
              <div><Label className="text-xs uppercase tracking-wide">ETA</Label><Input type="date" className="mt-1.5 rounded-sm" value={cForm.eta} onChange={(e) => setCForm({ ...cForm, eta: e.target.value })} data-testid="consol-ship-eta" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Origin</Label><Input className="mt-1.5 rounded-sm" value={cForm.origin} onChange={(e) => setCForm({ ...cForm, origin: e.target.value })} data-testid="consol-ship-origin" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Destination</Label><Input className="mt-1.5 rounded-sm" value={cForm.destination} onChange={(e) => setCForm({ ...cForm, destination: e.target.value })} data-testid="consol-ship-destination" /></div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createConsolidated} disabled={cBusy} data-testid="submit-consol-shipment">{cBusy ? "Creating…" : `Create shipment (${cOrderIds.length})`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mOpen} onOpenChange={setMOpen}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Merge shipments</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-sm border border-border bg-muted/40 p-3 text-sm" data-testid="merge-summary">
              <p className="font-medium">{mergeSel.length} shipments · {mAnchor?.client_name}</p>
              <p className="text-xs text-muted-foreground mt-1">{selShips.map((s) => s.shipment_number).join(", ")}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Their orders, products, photos, documents and history will be combined into one new shipment; the originals will be removed.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wide">Carrier</Label>
                <Select value={mForm.carrier} onValueChange={(v) => setMForm({ ...mForm, carrier: v })}>
                  <SelectTrigger className="mt-1.5 rounded-sm" data-testid="merge-carrier"><SelectValue placeholder="Select carrier" /></SelectTrigger>
                  <SelectContent>{CARRIER_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}><CarrierBadge carrier={c.code} /></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wide">AWB / Tracking #</Label><Input className="mt-1.5 rounded-sm" value={mForm.tracking_number} onChange={(e) => setMForm({ ...mForm, tracking_number: e.target.value })} data-testid="merge-tracking" /></div>
              <div><Label className="text-xs uppercase tracking-wide">ETA</Label><Input type="date" className="mt-1.5 rounded-sm" value={mForm.eta} onChange={(e) => setMForm({ ...mForm, eta: e.target.value })} data-testid="merge-eta" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Origin</Label><Input className="mt-1.5 rounded-sm" value={mForm.origin} onChange={(e) => setMForm({ ...mForm, origin: e.target.value })} data-testid="merge-origin" /></div>
              <div><Label className="text-xs uppercase tracking-wide">Destination</Label><Input className="mt-1.5 rounded-sm" value={mForm.destination} onChange={(e) => setMForm({ ...mForm, destination: e.target.value })} data-testid="merge-destination" /></div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={doMerge} disabled={mBusy || mergeSel.length < 2} data-testid="submit-merge">{mBusy ? "Merging…" : `Merge ${mergeSel.length} shipments`}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
